import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useIntl from '@i18n/useIntl';
import useChrome from '@chrome/hooks/useChrome';
import { ChromeKeys, MenuItemIds } from '@chrome/enums';
import { useGetAccountQuery } from '@services/account/account';
import { useGetConfigurationQuery } from '@services/configuration';
import { getAccountSideNavigationItems } from '@chrome/configuration/sideNavigation';
import { setSideNavigation } from '@chrome/api';
import Spinner from '@components/Spinner';
import { ErrorBanner } from '@components/notifications/banner/Banners';
import AccountDetails from 'src/pages/accountSettings/components/AccountDetails/AccountDetails';
import {
  ACCOUNT_SETTINGS_PAGE_ID,
  GENERAL_ERROR_BANNER_MESSAGE_KEY,
} from 'src/pages/accountSettings/constants';
import AccountDetailsSellingAccountList from 'src/pages/accountSettings/components/AccountDetails/AccountDetailsSellingAccountList';
import {
  AccountSettingsCard,
  AccountSettingsCardContent,
  AccountSettingsDivider,
} from 'src/pages/accountSettings/style';
import AccountName from 'src/pages/accountSettings/components/AccountDetails/AccountName';
import AccountEmptySellingAccount from 'src/pages/accountSettings/components/AccountDetails/AccountEmptySellingAccount';
import { setIsSga } from 'src/shared/state/account-settings-slice';
import { useDispatch } from 'react-redux';
import { EntityType } from '@models/account';
import AccountSettingsBanner from 'src/pages/accountSettings/components/AccountSettingsBanner';
import isEmpty from 'lodash/isEmpty';
import AccountLinkedSellingAccountWithoutAccess from 'src/pages/accountSettings/components/AccountDetails/AccountLinkedSellingAccountWithoutAccess';

const AccountSettingsPage = () => {
  const dispatch = useDispatch();
  const { intl } = useIntl();
  const navigate = useNavigate();
  const { entityId } = useParams();
  const { data: account, isLoading, error } = useGetAccountQuery(entityId!);
  const { data: { marketplaceId = '', globalAccountId = '' } = {} } =
    useGetConfigurationQuery();

  const { updateHeader } = useChrome(
    ChromeKeys.AccountSettings,
    () => {
      if (account?.displayName) {
        updateHeader({ name: account.displayName });
      }
    },
    [account?.displayName],
  );

  useEffect(() => {
    if (account && account.entityType) {
      // An SGA is defined as an entity where the entity type is DSP_ADVERTISING_ACCOUNT, and it has an associated GA ID
      dispatch(setIsSga(account.entityType === EntityType.Adsp));
    }
  }, [account?.entityType]);

  useEffect(() => {
    if (!account) {
      return;
    }

    const isDspOnlyAccount = !account.saEntityId;

    // The default nav rendered before account data loads is only correct when the
    // account's Sponsored Ads entity is the entity being browsed. Otherwise replace it:
    // either there is no SA entity (DSP-only account) or the SA entity lives on
    // another marketplace's console.
    if (!isDspOnlyAccount && account.saEntityId === entityId) {
      return;
    }

    setSideNavigation({
      items: getAccountSideNavigationItems({
        intl,
        entityId: entityId!,
        globalAccountId,
        navigate,
        saEntityId: account.saEntityId,
        saEntityUrl: account.saEntityUrl,
        isDspOnlyAccount,
        managerAccountId: account.managerAccountId,
        dspEntityId: account.dspEntityId,
      }),
      selected: MenuItemIds.AccountDetails,
    });
  }, [
    account?.saEntityId,
    account?.saEntityUrl,
    account?.managerAccountId,
    account?.dspEntityId,
    intl,
    entityId,
    globalAccountId,
    navigate,
  ]);

  if (error) {
    return (
      <ErrorBanner
        id={ACCOUNT_SETTINGS_PAGE_ID}
        message={intl.getMessage(GENERAL_ERROR_BANNER_MESSAGE_KEY)}
        duration={0}
      />
    );
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div id={ACCOUNT_SETTINGS_PAGE_ID} data-testid={ACCOUNT_SETTINGS_PAGE_ID}>
      <AccountSettingsBanner status={account?.businessVerificationStatus} />
      {account && (
        <>
          <AccountSettingsCard padding="none">
            <AccountSettingsCardContent>
              {!isEmpty(account.sellingAccounts) ? (
                <AccountDetailsSellingAccountList
                  sellingAccounts={account.sellingAccounts}
                />
              ) : !account.isLinkedToSellingAccount ? (
                <AccountEmptySellingAccount
                  saEntityId={account.saEntityId}
                  saEntityMarketplaceId={account.saEntityMarketplaceId}
                  saEntityUrl={account.saEntityUrl}
                  marketplaceId={marketplaceId}
                />
              ) : (
                <AccountLinkedSellingAccountWithoutAccess />
              )}
            </AccountSettingsCardContent>
          </AccountSettingsCard>
          <AccountSettingsCard padding="none">
            <AccountSettingsCardContent>
              <AccountDetails
                business={account.business}
                currency={account.currency}
                timeZone={account.timeZone}
                industry={account.industry}
              ></AccountDetails>
            </AccountSettingsCardContent>
            <AccountSettingsDivider />
            <AccountSettingsCardContent>
              <AccountName displayName={account.displayName}></AccountName>
            </AccountSettingsCardContent>
          </AccountSettingsCard>
        </>
      )}
    </div>
  );
};

export default AccountSettingsPage;
